import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PatternBased, RuleBased, RuleSet } from '@kkp-fraud/shared';
import { api, isServerConfigError, serverConfigMessage, type ApiSettings } from '../api';
// R8b per-rule-set permissions
import { ruleSetForbiddenMessage } from '../api';
import { computeDiff, type RuleSetDiff } from '../diff';
import { useRuleCheck, type RuleCheck } from './useRuleCheck';

/**
 * The one piece of state the whole app shares: the document the engine is running
 * (`loaded`) and the document on screen (`draft`). Everything else — the dirty flag, the
 * diff, the engine's verdict on the draft — is derived, so nothing can drift out of step.
 */

export type RuleKind = 'ruleBased' | 'patternBased';

export interface RuleSetDraft {
  loaded: RuleSet | null;
  draft: RuleSet | null;
  source: 'file' | 'cosmos' | null;
  loadedAt: string | null;
  loading: boolean;
  error: string | null;
  diff: RuleSetDiff;
  dirty: boolean;
  /**
   * The engine's check of the draft (Wave 2-0): asked 400 ms after the last edit and by the
   * "Check rules" button; publishing needs `check.canSave`. Off while the draft is clean, and
   * for an account that may not publish — a Viewer never triggers it.
   */
  check: RuleCheck;
  /** Refetch the active document; resolves with it (null when the fetch failed). */
  reload: () => Promise<RuleSet | null>;
  /** Replace the draft after a successful PUT, so the diff goes empty again. */
  commit: (saved: RuleSet) => void;
  revert: () => void;
  setDraft: (next: RuleSet) => void;
  patchRuleSet: (patch: Partial<RuleSet>) => void;
  /**
   * Replace the rule with `rule.id` (or append it). With `previousId` and a renamed rule, the
   * rule at `previousId` is replaced in place — same position, no copy left behind.
   */
  upsertRule: (kind: RuleKind, rule: RuleBased | PatternBased, previousId?: string) => void;
  removeRule: (kind: RuleKind, id: string) => void;
  toggleEnabled: (kind: RuleKind, id: string) => void;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export interface RuleSetDraftOptions {
  /** Ask the engine about the draft as it changes. False for a Viewer (read-only screens). */
  checkRules?: boolean;
  /** 2c rule sets — false: load nothing (the shell's second draft while no other set is chosen). */
  load?: boolean;
}

export function useRuleSetDraft(settings: ApiSettings, options: RuleSetDraftOptions = {}): RuleSetDraft {
  const { checkRules = true, load = true } = options;
  const [loaded, setLoaded] = useState<RuleSet | null>(null);
  const [draft, setDraftState] = useState<RuleSet | null>(null);
  const [source, setSource] = useState<'file' | 'cosmos' | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<RuleSet | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getRules(settings);
      setLoaded(res.ruleSet);
      setDraftState(clone(res.ruleSet));
      setSource(res.source);
      setLoadedAt(res.loadedAt);
      return res.ruleSet;
    } catch (e) {
      // A 502 `upstream_auth` / 503 `admin_not_configured` is the BFF's own configuration,
      // not this document — say so, instead of showing a bare upstream message.
      // R8b: a set this account may not see says so in words, not as the service's English.
      setError(isServerConfigError(e) ? serverConfigMessage(e) : (ruleSetForbiddenMessage(e) ?? (e as Error).message));
      return null;
    } finally {
      setLoading(false);
    }
    // 2c rule sets: another set is another document — `ruleSetId` refetches.
  }, [settings.baseUrl, settings.ruleSetId]);

  useEffect(() => {
    if (!load) {
      // 2c rule sets: nothing chosen — no document, and nothing left of a previous set's.
      setLoaded(null);
      setDraftState(null);
      setLoading(false);
      return;
    }
    void reload();
  }, [reload, load]);

  const setDraft = useCallback((next: RuleSet) => setDraftState(next), []);

  const patchRuleSet = useCallback((patch: Partial<RuleSet>) => {
    setDraftState((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const upsertRule = useCallback((kind: RuleKind, rule: RuleBased | PatternBased, previousId?: string) => {
    setDraftState((prev) => (prev ? upsertRuleIn(prev, kind, rule, previousId) : prev));
  }, []);

  const removeRule = useCallback((kind: RuleKind, id: string) => {
    setDraftState((prev) => {
      if (!prev) return prev;
      const list = ((prev[kind] ?? []) as (RuleBased | PatternBased)[]).filter((r) => r.id !== id);
      return { ...prev, [kind]: list } as RuleSet;
    });
  }, []);

  const toggleEnabled = useCallback((kind: RuleKind, id: string) => {
    setDraftState((prev) => {
      if (!prev) return prev;
      const list = ((prev[kind] ?? []) as (RuleBased | PatternBased)[]).map((r) =>
        r.id === id ? { ...r, enabled: !r.enabled } : r,
      );
      return { ...prev, [kind]: list } as RuleSet;
    });
  }, []);

  const revert = useCallback(() => {
    setDraftState(loaded ? clone(loaded) : null);
  }, [loaded]);

  const commit = useCallback((saved: RuleSet) => {
    setLoaded(saved);
    setDraftState(clone(saved));
    setLoadedAt(new Date().toISOString());
  }, []);

  const diff = useMemo(() => computeDiff(loaded, draft), [loaded, draft]);
  // Only an edited draft is checked: the live document is what the engine already runs, and
  // there is nothing to publish until something changed.
  const check = useRuleCheck(settings, draft, { enabled: checkRules && diff.dirty });

  return {
    loaded,
    draft,
    source,
    loadedAt,
    loading,
    error,
    diff,
    dirty: diff.dirty,
    check,
    reload,
    commit,
    revert,
    setDraft,
    patchRuleSet,
    upsertRule,
    removeRule,
    toggleEnabled,
  };
}

/**
 * `rs` with `rule` written into `kind`: at the position of `previousId` when the rule was
 * renamed and that id is still there, otherwise at its own id, otherwise appended. A copy.
 */
export function upsertRuleIn(rs: RuleSet, kind: RuleKind, rule: RuleBased | PatternBased, previousId?: string): RuleSet {
  const list = [...((rs[kind] ?? []) as (RuleBased | PatternBased)[])];
  list[upsertedIndex(rs, kind, rule, previousId)] = rule;
  return { ...rs, [kind]: list } as RuleSet;
}

/** Where `upsertRuleIn` puts `rule` in `kind` — the index the engine's paths name it by. */
export function upsertedIndex(rs: RuleSet, kind: RuleKind, rule: RuleBased | PatternBased, previousId?: string): number {
  const list = (rs[kind] ?? []) as (RuleBased | PatternBased)[];
  const renamedAt = previousId && previousId !== rule.id ? list.findIndex((r) => r.id === previousId) : -1;
  const at = renamedAt >= 0 ? renamedAt : list.findIndex((r) => r.id === rule.id);
  return at >= 0 ? at : list.length;
}

/**
 * A blank rule of each family, pre-filled the way §6.4 expects. `points` is never shown or
 * used (no scoring since 30 Sep 2026) but the document still requires a number ≥ 0
 * (`points_invalid`), so a new rule carries one.
 */
export function newRuleBased(existingIds: readonly string[]): RuleBased {
  return {
    id: nextId('RB', existingIds),
    name: '',
    priority: 'P2',
    action: 'STEP_UP',
    points: 50,
    enabled: false,
    thresholdConfirmed: false,
    clauses: [{ field: 'amount_thb', op: 'gt', value: 0 }],
    rationale: '',
  };
}

export function newPatternBased(existingIds: readonly string[]): PatternBased {
  return {
    id: nextId('PB', existingIds),
    name: '',
    priority: 'P2',
    action: 'STEP_UP',
    points: 50,
    enabled: false,
    thresholdConfirmed: false,
    scope: 'card_id',
    event: 'approved',
    aggregate: 'count',
    threshold: 3,
    windowSeconds: 600,
    filters: [],
    rationale: '',
  };
}

/** RB-01, RB-02 … → the first free number in the same style. */
export function nextId(prefix: 'RB' | 'PB', existingIds: readonly string[]): string {
  const used = new Set(existingIds);
  for (let n = 1; n < 1000; n++) {
    const id = `${prefix}-${String(n).padStart(2, '0')}`;
    if (!used.has(id)) return id;
  }
  return `${prefix}-${Date.now()}`;
}
